import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { useAppStore } from '@/store/app'
import { useSocialStore } from '@/store/social'
import { flushReading, readPosition, readingAnchor, restoreAnchor, savePosition, type ReadingPosition } from '@/lib/chatReading'

/** Shared by DMs, server channels, forum threads and the embedded voice chat. */
export function useChatReading(userId: string | undefined, channelId: string, scroller: RefObject<HTMLDivElement | null>, stick: RefObject<boolean>, detached: boolean, jumping: boolean, enabled: boolean, onError: (error: unknown) => void) {
  const messages = useSocialStore((s) => s.messages[channelId])
  const [initial] = useState(() => useAppStore.getState().jumpTarget?.channelId === channelId ? undefined : readPosition(userId, channelId))
  const anchor = useRef<ReadingPosition | undefined>(initial)
  const pending = useRef(Boolean(initial?.messageId && (!initial.bottom || initial.detached)))
  const waiting = useRef(pending.current && !useSocialStore.getState().messages[channelId]?.some((m) => m.id === initial?.messageId))
  const [restoreFinished, setRestoreFinished] = useState(!waiting.current)
  const [ready, setReady] = useState(!pending.current)
  const [atBottom, setAtBottom] = useState(!pending.current)
  const [restoredWindow, setRestoredWindow] = useState(Boolean(initial?.detached))
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  const state = useRef({ detached, jumping, enabled, restoredWindow, onError })
  useLayoutEffect(() => { state.current = { detached, jumping, enabled, restoredWindow, onError } })
  const initialized = useRef(false)
  if (!initialized.current) { initialized.current = true; if (pending.current) stick.current = false }

  const remember = useCallback(() => {
    const el = scroller.current
    const s = state.current
    if (!el || !el.clientHeight || pending.current || s.jumping || !s.enabled || busyRef.current) return
    const bottom = stick.current
    anchor.current = { ...readingAnchor(el), bottom, detached: s.detached || s.restoredWindow }
    savePosition(userId, channelId, anchor.current)
  }, [channelId, userId, scroller, stick])

  // Start the anchor request before the caller's initial history request.
  useEffect(() => {
    if (!pending.current || !initial?.messageId || !enabled) return
    let alive = true
    const social = useSocialStore.getState()
    if (social.messages[channelId]?.some((m) => m.id === initial.messageId)) {
      waiting.current = false
      setRestoreFinished(true)
      return
    }
    setRestoredWindow(true)
    void social.loadAround(channelId, initial.messageId).then(() => {
      if (!alive) return
      waiting.current = false
      setRestoreFinished(true)
    }).catch((error: unknown) => {
      if (!alive) return
      waiting.current = false
      pending.current = false
      setRestoreFinished(true)
      setReady(true)
      state.current.onError(error)
    })
    return () => { alive = false }
  }, [channelId, initial, enabled])

  useLayoutEffect(() => {
    const el = scroller.current
    if (!el?.clientHeight || !enabled) return
    if (jumping) { anchor.current = undefined; pending.current = false; setReady(true); setAtBottom(false); return }
    if (waiting.current || !messages) return
    if (pending.current) {
      // Do not replace a saved anchor with the unrelated cached history while its request runs.
      if (useSocialStore.getState().loading[channelId]) return
      if (anchor.current) restoreAnchor(el, anchor.current)
      pending.current = false
      setReady(true)
    } else if (stick.current) el.scrollTop = el.scrollHeight
    else if (anchor.current) restoreAnchor(el, anchor.current)
    remember()
  }, [messages, enabled, jumping, channelId, scroller, stick, remember, restoreFinished])

  const ref = useCallback((el: HTMLDivElement | null) => {
    scroller.current = el
    if (!el) return
    const observer = new ResizeObserver(() => {
      if (!el.clientHeight || pending.current || state.current.jumping || busyRef.current) return
      if (stick.current) el.scrollTop = el.scrollHeight
      else if (anchor.current) restoreAnchor(el, anchor.current)
      remember()
    })
    observer.observe(el)
    if (el.firstElementChild) observer.observe(el.firstElementChild)
    return () => {
      remember()
      flushReading(userId)
      observer.disconnect()
      scroller.current = null
    }
  }, [scroller, stick, remember, userId])

  const onScroll = useCallback(() => {
    const el = scroller.current
    if (!el || !el.clientHeight || pending.current || busyRef.current) return
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40
    setAtBottom(stick.current)
    remember()
  }, [scroller, stick, remember])

  useEffect(() => {
    const flush = () => { remember(); flushReading(userId) }
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', flush)
    return () => {
      flush()
      window.removeEventListener('pagehide', flush)
      document.removeEventListener('visibilitychange', flush)
    }
  }, [remember, userId])

  const toLatest = useCallback(async (loadLatest: (reload: boolean) => Promise<void>) => {
    if (busyRef.current) return
    busyRef.current = true
    setBusy(true)
    try {
      // A history window must be replaced before scrolling or acknowledging anything.
      await loadLatest(state.current.detached || state.current.restoredWindow)
      stick.current = true
      pending.current = false
      waiting.current = false
      setRestoredWindow(false)
      state.current.restoredWindow = false
      state.current.detached = false
      setReady(true)
      setAtBottom(true)
      const el = scroller.current
      if (el) el.scrollTop = el.scrollHeight
    } finally {
      busyRef.current = false
      setBusy(false)
      remember()
    }
  }, [stick, scroller, remember])

  return { ref, onScroll, remember, toLatest, busy, ready, atLatest: ready && atBottom && !jumping && !detached && !restoredWindow, showLatest: !atBottom || detached || restoredWindow }
}
